import { createReducer } from "typesafe-actions";
import { loginAsync, registerRestaurantAsync, logoutAsync, refreshTokenAsync, abnLookupAsync, resetSignUpForm, registerSupplierAsync, resetPasswordEmailAsync, updateUserAutheticatedInfo, resetPasswordConfirmAsync, updateSignUpData } from "../actions/AuthActions";
import { Throwing } from "../../models/common";
import { AbnLookupResult } from "../../api/models/AbnLookupResult";
import * as firebase from 'firebase/app'
import { Supplier } from "../../api/models/QuotedData";
import { Restaurant } from "../../api/models/RestaurantData";
import { LocalStorageWrapper } from "../../LocalStorage/LocalStorageWrapper";

export type UserRole = "Administrator" | "Restaurant" | "Supplier" | null


export interface AuthState extends Throwing {
    isAuthenticated: boolean | null,
    userRole: UserRole,
    userDisplayName: string | null,
    loginErrorMessage: string,
    lastRefresh: Date,
    accountCreatedSuccessfully: boolean | null
    signUpBusy: boolean,
    refreshTokenBusy: boolean,

    abnDetails: AbnLookupResult | null,
    abnLookupBusy: boolean,
    passwordResetEmailSendMessage: string,
    passwordResetEmailSendSuccess: boolean | null,

    passwordResetConfirmMessage: string,
    passwordResetConfirmSuccess: boolean | null,
    passwordResetConfirmBusy: boolean,

    signUpData: Supplier | Restaurant | null,
}

const initialState: AuthState = {
    isAuthenticated: null,
    userRole: null,
    userDisplayName: null,
    loginErrorMessage: '',
    lastRefresh: new Date(),
    accountCreatedSuccessfully: null,
    signUpBusy: false,
    refreshTokenBusy: false,

    abnDetails: null,
    abnLookupBusy: false,
    passwordResetEmailSendMessage: '',
    passwordResetEmailSendSuccess: null,

    passwordResetConfirmMessage: '',
    passwordResetConfirmSuccess: null,
    passwordResetConfirmBusy: false,

    signUpData: null,
}

const getUserRole = (tokenResult: firebase.auth.IdTokenResult) => {
    if (tokenResult.claims["qmo_role"] === undefined) {
        throw new Error("Your account is corrupted. Contact the administrator.")
    }
    else return tokenResult.claims["qmo_role"][0]
}

export const authReducer = createReducer(initialState)
    .handleAction(loginAsync.request, (state: AuthState) => { return state })
    .handleAction(loginAsync.success, (state: AuthState, action) => {
        LocalStorageWrapper.Set("id_token", action.payload.tokenResult.token)

        return { ...state, data: action.payload, isAuthenticated: true, loginErrorMessage: '', lastRefresh: new Date(), userDisplayName: action.payload.displayName, userRole: getUserRole(action.payload.tokenResult) }
    })
    .handleAction(loginAsync.failure, (state: AuthState, action) => { return { ...state, error: action.payload, loginErrorMessage: action.payload.message } })

    .handleAction(registerRestaurantAsync.request, (state: AuthState) => {
        return { ...state, signUpBusy: true, accountCreatedSuccessfully: null }
    })
    .handleAction(registerRestaurantAsync.success, (state: AuthState, action) => {
        LocalStorageWrapper.Set("id_token", action.payload)
        return { ...state, signUpBusy: false, accountCreatedSuccessfully: true }
    })
    .handleAction(registerRestaurantAsync.failure, (state: AuthState, action) => {
        return { ...state, signUpBusy: false, accountCreatedSuccessfully: false, error: action.payload };
    })


    .handleAction(registerSupplierAsync.request, (state: AuthState) => {
        return { ...state, signUpBusy: true, accountCreatedSuccessfully: null }
    })
    .handleAction(registerSupplierAsync.success, (state: AuthState, action) => {
        LocalStorageWrapper.Set("id_token", action.payload)
        return { ...state, signUpBusy: false, accountCreatedSuccessfully: true }
    })
    .handleAction(registerSupplierAsync.failure, (state: AuthState, action) => {
        return { ...state, signUpBusy: false, accountCreatedSuccessfully: false, error: action.payload };
    })



    .handleAction(refreshTokenAsync.request, (state: AuthState, action) => {
        return { ...state, refreshTokenBusy: true }
    })
    .handleAction(refreshTokenAsync.success, (state: AuthState, action) => {
        LocalStorageWrapper.Set("id_token", action.payload.tokenResult.token)

        console.log('Token rewritten')
        return { ...state, lastRefresh: new Date(), refreshTokenBusy: false, isAuthenticated: true, userDisplayName: action.payload.displayName, userRole: getUserRole(action.payload.tokenResult) }
    })
    .handleAction(refreshTokenAsync.failure, (state: AuthState, action) => { return { ...state, error: action.payload, refreshTokenBusy: false, isAuthenticated: false } })


    .handleAction(logoutAsync.request, (state: AuthState) => { return state })
    .handleAction(logoutAsync.success, (state: AuthState) => {
        LocalStorageWrapper.Remove("id_token")
        
        return { ...state, isAuthenticated: false }
    })
    .handleAction(logoutAsync.failure, (state: AuthState, action) => {
        return { ...state, error: action.payload }
    })

    .handleAction(resetPasswordEmailAsync.request, (state: AuthState) => { return { ...state, passwordResetEmailSendSuccess: null } })
    .handleAction(resetPasswordEmailAsync.success, (state: AuthState) => {
        return { ...state, passwordResetEmailSendMessage: 'Email with password reset link was sent to provided address. Please check your inbox.', passwordResetEmailSendSuccess: true }
    })
    .handleAction(resetPasswordEmailAsync.failure, (state: AuthState, action) => {
        return { ...state, error: action.payload, passwordResetEmailSendSuccess: false, passwordResetEmailSendMessage: action.payload.message }
    })


    .handleAction(resetPasswordConfirmAsync.request, (state: AuthState) => { return { ...state, passwordResetConfirmSuccess: null, passwordResetConfirmBusy: true } })
    .handleAction(resetPasswordConfirmAsync.success, (state: AuthState) => {
        return { ...state, passwordResetConfirmMessage: 'Your password have been changed successfully.', passwordResetConfirmSuccess: true, passwordResetConfirmBusy: false }
    })
    .handleAction(resetPasswordConfirmAsync.failure, (state: AuthState, action) => {
        return { ...state, error: action.payload, passwordResetConfirmSuccess: false, passwordResetConfirmMessage: action.payload.message, passwordResetConfirmBusy: false }
    })


    .handleAction(abnLookupAsync.request, (state: AuthState) => { return { ...state, abnLookupBusy: true, abnDetails: null } })
    .handleAction(abnLookupAsync.success, (state: AuthState, action) => {
        return { ...state, abnDetails: (action.payload as AbnLookupResult), abnLookupBusy: false }
    })
    .handleAction(abnLookupAsync.failure, (state: AuthState, action) => {
        return { ...state, error: action.payload, abnLookupBusy: false }
    })


    .handleAction(resetSignUpForm, (state: AuthState) => {
        return { ...state, abnDetails: null }
    })

    .handleAction(updateUserAutheticatedInfo, (state: AuthState, action) => {
        return { ...state, isAuthenticated: action.payload }
    })

    .handleAction(updateSignUpData, (state: AuthState, action) => {
        return { ...state, signUpData: action.payload }
    })

export default authReducer;